"use client";

import { useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";

import { AdminDialog, type AdminDialogSize } from "@/components/admin/admin-dialog-layout";
import {
  AdminDeleteConfirmPopover,
  isPointerAnchorFresh,
  type AdminDeleteConfirmAnchor,
} from "@/components/admin/admin-delete-confirm-popover";

function renderParagraph(value: ReactNode) {
  if (value == null || value === false) {
    return null;
  }

  if (typeof value === "string") {
    return <p>{value}</p>;
  }

  return value;
}

export type AdminDeleteConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  title?: ReactNode;

  intro?: ReactNode;

  content?: ReactNode;

  children?: ReactNode;
  saving?: boolean;
  disabled?: boolean;
  cancelDisabled?: boolean;
  confirmLabel?: string;
  deletingLabel?: string;
  onCancel?: () => void;
  onConfirm: () => void;
  className?: string;
  size?: AdminDialogSize;

  anchor?: AdminDeleteConfirmAnchor;

  align?: "start" | "center" | "end";

  showSaveHint?: boolean;
};

export function AdminDeleteConfirmDialog({
  open,
  onOpenChange,
  title,
  intro,
  content,
  children,
  saving = false,
  disabled = false,
  cancelDisabled,
  confirmLabel,
  deletingLabel,
  onCancel,
  onConfirm,
  className,
  size = "sm",
  anchor,
  align,
  showSaveHint = false,
}: AdminDeleteConfirmDialogProps) {
  const t = useTranslations("site.ui");

  const canUsePopover = () => anchor != null && (anchor !== "pointer" || isPointerAnchorFresh());
  const [placement, setPlacement] = useState(() => ({ open, popover: canUsePopover() }));
  if (placement.open !== open) {
    setPlacement({ open, popover: open ? canUsePopover() : placement.popover });
  }

  if (placement.popover && anchor != null) {
    const bodyText = content ?? intro;

    return (
      <AdminDeleteConfirmPopover
        open={open}
        onOpenChange={onOpenChange}
        anchor={anchor}
        align={align}
        title={title ?? t("confirmPopoverTitle")}
        content={bodyText ?? t("deleteConfirmQuestion")}
        showSaveHint={showSaveHint}
        saving={saving}
        disabled={disabled}
        cancelDisabled={cancelDisabled}
        confirmLabel={confirmLabel}
        deletingLabel={deletingLabel}
        onCancel={onCancel}
        onConfirm={onConfirm}
      />
    );
  }

  const body =
    children ?? (
      <>
        {intro == null && content == null ? <p>{t("deleteConfirmQuestion")}{showSaveHint ? <> {t("changesApplyAfterSave")}</> : null}</p> : null}
        {renderParagraph(intro)}
        {renderParagraph(content)}
        {showSaveHint && (intro != null || content != null) ? <p>{t("changesApplyAfterSave")}</p> : null}
      </>
    );

  return (
    <AdminDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title ?? t("deleteConfirmTitle")}
      size={size}
      className={className}
      saving={saving}
      cancel={{
        disabled: cancelDisabled ?? disabled,
        onClick: () => {
          onCancel?.();
          onOpenChange(false);
        },
      }}
      confirm={{
        destructive: true,
        disabled,
        label: confirmLabel,
        savingLabel: deletingLabel,
        onClick: onConfirm,
      }}
    >
      {body}
    </AdminDialog>
  );
}
